import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { cs } from 'date-fns/locale';

import { CtaBtn, SecondaryBtn } from '@tmapy/style-guide';

import './DateRangePickerBtn.scss';

const dateRangePickerBtnMessages = defineMessages({
  title: {
    id: 'dateRangePickerBtn.title',
    defaultMessage: 'Vyberte rozsah',
  },
  btnCancel: {
    id: 'dateRangePickerBtn.btn.cancel',
    defaultMessage: 'Zrušit',
  },
  btnSubmit: {
    id: 'dateRangePickerBtn.btn.submit',
    defaultMessage: 'Potvrdit',
  },
});

export type DateRangeData = {
  start: Date;
  end: Date;
  startTimestamp: number;
  endTimestamp: number;
};

type DateRangePickerBtnProps = {
  label?: string;
  onConfirm: (data: DateRangeData) => void;
};

registerLocale('cs', cs);

const SELECTABLE_YEARS_BACK = 10;

export const DateRangePickerBtn: React.FC<DateRangePickerBtnProps> = ({ onConfirm, label }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const today = new Date();
  const minDate = new Date(today.getFullYear() - SELECTABLE_YEARS_BACK, 0, 1);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleConfirm = () => {
    if (!startDate || !endDate) {
      return;
    }

    onConfirm({
      start: startDate,
      end: endDate,
      startTimestamp: startDate.getTime(),
      endTimestamp: endDate.getTime(),
    });

    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setDateRange([null, null]);
  };

  const handleToggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className='datepicker-container' ref={containerRef}>
      <SecondaryBtn size="sml" onClick={handleToggleIsOpen}>
        {label}
      </SecondaryBtn>

      {isOpen && (
        <div className='datepicker-popover'>
          <div className='datepicker-header'>
            <FormattedMessage {...dateRangePickerBtnMessages.title} />
          </div>

          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            inline
            locale='cs'
            dateFormat='dd.MM.yyyy'
            minDate={minDate}
            maxDate={today}
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
          />

          <div className='datepicker-actions'>
            <SecondaryBtn onClick={handleCancel}>
              <FormattedMessage {...dateRangePickerBtnMessages.btnCancel} />
            </SecondaryBtn>
            <CtaBtn isDisabled={!startDate || !endDate} onClick={handleConfirm}>
              <FormattedMessage {...dateRangePickerBtnMessages.btnSubmit} />
            </CtaBtn>
          </div>
        </div>
      )}
    </div>
  );
};
